import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AllocationOverview } from '../api.js';
import { FormField } from './FormField.js';
import { FormRow } from './FormRow.js';
export { CREDITS_PATTERN, MILLIS_PER_CREDIT, toCredits, toMillis } from '../utils/format.js';
import { toCredits } from '../utils/format.js';

/**
 * Lowest value the server will accept for this grantee, in credits.
 *
 * Mirrors the two floors `allocateCreditInTx` enforces — `allocation_below_consumed`
 * (lifetime consumption) and `allocation_below_suballocated` (sum of finite child
 * grants). Surfacing it beats letting the operator discover it through a 409:
 * an allocation is the cumulative total ever granted, so a grantee that ran
 * unallocated for a while has a floor equal to everything it has already spent.
 */
function allocationFloor(overview: AllocationOverview | undefined): number {
  if (!overview || overview.unlimited) return 0;
  const suballocated = overview.children.reduce(
    (sum, child) => sum + (child.unlimited ? 0 : (child.creditGranted ?? 0)),
    0
  );
  return toCredits(Math.max(overview.credit.consumed, suballocated));
}

export function AllocationPanel({
  label,
  overview,
  credit,
  unlimited,
  allowUnlimited,
  busy,
  onCreditChange,
  onUnlimitedChange,
  onSave,
  framed = true
}: {
  label: string;
  overview?: AllocationOverview;
  credit: string;
  unlimited: boolean;
  allowUnlimited: boolean;
  busy: boolean;
  onCreditChange: (credit: string) => void;
  onUnlimitedChange?: (unlimited: boolean) => void;
  onSave: (event: FormEvent) => void;
  framed?: boolean;
}) {
  const { t } = useTranslation();
  const floor = allocationFloor(overview);
  const showFloor = floor > 0 && !(allowUnlimited && unlimited);

  return (
    <FormRow className="allocation-form" framed={framed} onSubmit={onSave}>
      <h2>{label}</h2>
      {overview && (
        <span className="muted">
          {overview.unlimited
            ? t('allocationPanel.unlimitedInfo', {
                consumed: toCredits(overview.credit.consumed)
              })
            : t('allocationPanel.info', {
                granted: overview.credit.granted === undefined ? 0 : toCredits(overview.credit.granted),
                consumed: toCredits(overview.credit.consumed),
                remaining: overview.credit.remaining === undefined ? 0 : toCredits(overview.credit.remaining)
              })}
        </span>
      )}
      <FormField label={t('allocationPanel.credits')}
        value={credit}
        inputMode="decimal"
        placeholder={t('allocationPanel.credits')}
        disabled={allowUnlimited && unlimited}
        onChange={(event) => onCreditChange(event.target.value)}
        autoComplete="off"
      />
      {showFloor && (
        <>
          <span className="muted">{t('allocationPanel.floorHint', { floor })}</span>
          <button type="button" disabled={busy} onClick={() => onCreditChange(String(floor))}>
            {t('allocationPanel.useFloor')}
          </button>
        </>
      )}
      {allowUnlimited && (
        <label className="check-row">
          <input type="checkbox" checked={unlimited} onChange={(event) => onUnlimitedChange?.(event.target.checked)} />
          <span>{t('allocationPanel.unlimited')}</span>
        </label>
      )}
      <button disabled={busy}>{t('allocationPanel.save')}</button>
    </FormRow>
  );
}
